import { z } from 'zod/v4';

const id = z.uuid();
/** P-9289 connectivity only. These records never confer installation approval. */
export const solarEquipmentSelectionSchema = z.object({
  id,
  applianceId: z.string().max(100).nullable(),
  productSpecId: z.string().max(100).nullable(),
  contentHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  /** Server-pinned catalogue content, including exact manufacturer/model/market and provenance. */
  specSnapshot: z.record(z.string(), z.json()).nullable(),
});

export const solarElectricalTopologySchema = z.object({
  mode: z.literal('allocated'),
  version: z.literal(1),
  equipmentSelections: z.array(solarEquipmentSelectionSchema).max(500),
  placementGroups: z
    .array(
      z.object({
        id,
        faceId: z.number().int().nonnegative(),
        equipmentSelectionId: id.nullable(),
      })
    )
    .max(1000),
  inverterInstances: z
    .array(
      z.object({
        id,
        name: z.string().trim().min(1).max(120),
        equipmentSelectionId: id,
        /** One conventional physical unit; never a combined controller/system rating. */
        quantity: z.literal(1),
        trackers: z
          .array(
            z.object({
              key: z.string().min(1).max(100),
              provenance: z.enum(['manufacturer_verified', 'user_declared']),
              inputs: z
                .array(z.object({ key: z.string().min(1).max(100) }))
                .max(100),
            })
          )
          .max(100),
      })
    )
    .max(500),
  strings: z
    .array(
      z.object({
        id,
        name: z.string().trim().min(1).max(120),
        panelIds: z.array(id).max(5000),
        inverterInstanceId: id.nullable(),
        mpptKey: z.string().max(100).nullable(),
        inputKey: z.string().max(100).nullable(),
        incompleteReason: z.enum(['panels_removed', 'repacked']).nullish(),
      })
    )
    .max(1000),
  /** P-9288 owns calculation results; relevant edits must clear/stale these. */
  validation: z
    .object({
      designRevision: z.number().int().nonnegative(),
      equipmentContentHashes: z.record(z.string(), z.string()),
      modelVersion: z.string(),
      ruleVersion: z.string(),
      calculatedAt: z.iso.datetime(),
      findings: z.array(
        z.object({
          ruleCode: z.string(),
          scopeId: id,
          status: z.enum(['pass', 'fail', 'unknown', 'unsupported', 'warning']),
          message: z.string(),
        })
      ),
    })
    .nullish(),
});

export type SolarElectricalTopology = z.infer<
  typeof solarElectricalTopologySchema
>;

export const solarDesignElectricalSchema = z.discriminatedUnion('mode', [
  /** Equipment chosen for the whole design, including micro and integrated storage. */
  z.object({ mode: z.literal('wholeDesign') }),
  solarElectricalTopologySchema,
]);
export type SolarDesignElectrical = z.infer<typeof solarDesignElectricalSchema>;

export const allocatedSolarElectrical = (
  electrical: SolarDesignElectrical | undefined
): SolarElectricalTopology | null =>
  electrical?.mode === 'allocated' ? electrical : null;
export type SolarEquipmentSelection = z.infer<
  typeof solarEquipmentSelectionSchema
>;
export type SolarIdentifiedPanel = {
  id?: string;
  equipmentSelectionId?: string;
  placementGroupId?: string;
  faceId: number;
  included: boolean;
};

/** Per-physical-unit allocation, using only saved ratings and active panel IDs. */
export const solarInverterAllocationSummary = (
  topology: SolarElectricalTopology,
  panels: readonly SolarIdentifiedPanel[],
  instanceId: string,
  excludedFaceIds: readonly number[] = []
) => {
  const instance = topology.inverterInstances.find(
    (item) => item.id === instanceId
  );
  const selections = new Map(
    topology.equipmentSelections.map((item) => [item.id, item])
  );
  const assignedIds = new Set(
    topology.strings
      .filter((item) => item.inverterInstanceId === instanceId)
      .flatMap((item) => item.panelIds)
  );
  const active = panels.filter(
    (panel) =>
      panel.included &&
      !excludedFaceIds.includes(panel.faceId) &&
      assignedIds.has(panel.id ?? '')
  );
  const rating = (
    selectionId: string | undefined,
    field: string
  ): number | null => {
    const specs = selections.get(selectionId ?? '')?.specSnapshot?.specs;
    const value =
      specs && typeof specs === 'object' && !Array.isArray(specs)
        ? specs[field]
        : null;
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? value
      : null;
  };
  const capacities = active.map((panel) =>
    rating(panel.equipmentSelectionId, 'panelCapacityW')
  );
  const allocatedDcKw = capacities.some((value) => value == null)
    ? null
    : capacities.reduce<number>((sum, value) => sum + (value ?? 0), 0) / 1000;
  const acOutputKw = rating(instance?.equipmentSelectionId, 'acOutputKw');
  return {
    panelCount: active.length,
    allocatedDcKw,
    acOutputKw,
    dcAcRatio:
      allocatedDcKw != null && acOutputKw != null
        ? allocatedDcKw / acOutputKw
        : null,
    status: 'requires_review' as const,
  };
};

/** Return errors rather than throwing in the editor. Inclusion never deletes editable membership. */
export const inspectSolarElectricalTopology = (
  topology: SolarElectricalTopology,
  panels: readonly SolarIdentifiedPanel[],
  faceIds: readonly number[],
  excludedFaceIds: readonly number[] = []
) => {
  const errors: string[] = [];
  const unique = <T extends { id?: string }>(
    items: readonly T[],
    label: string
  ) => {
    const ids = new Set(items.map((item) => item.id));
    if (ids.size !== items.length || ids.has(undefined))
      errors.push(`${label} identities must be present and unique.`);
  };
  unique(panels, 'Panel');
  unique(topology.equipmentSelections, 'Equipment selection');
  unique(topology.placementGroups, 'Placement group');
  unique(topology.inverterInstances, 'Inverter instance');
  unique(topology.strings, 'String');
  const panelById = new Map(panels.map((panel) => [panel.id, panel]));
  const selectionIds = new Set(
    topology.equipmentSelections.map((item) => item.id)
  );
  const groupById = new Map(
    topology.placementGroups.map((item) => [item.id, item])
  );
  const instanceById = new Map(
    topology.inverterInstances.map((item) => [item.id, item])
  );
  for (const group of topology.placementGroups) {
    if (!faceIds.includes(group.faceId))
      errors.push('A placement group references an unknown roof.');
    if (
      group.equipmentSelectionId &&
      !selectionIds.has(group.equipmentSelectionId)
    )
      errors.push('A placement group references unknown equipment.');
  }
  for (const panel of panels) {
    const group = groupById.get(panel.placementGroupId ?? '');
    if (
      !group ||
      group.faceId !== panel.faceId ||
      group.equipmentSelectionId !== (panel.equipmentSelectionId ?? null)
    )
      errors.push(
        'A panel must reference its own roof placement group and equipment selection.'
      );
  }
  for (const instance of topology.inverterInstances) {
    if (!selectionIds.has(instance.equipmentSelectionId))
      errors.push('An inverter instance references unknown equipment.');
    if (
      new Set(instance.trackers.map((tracker) => tracker.key)).size !==
      instance.trackers.length
    )
      errors.push('MPPT keys must be unique within an inverter.');
    const inputs = instance.trackers.flatMap((tracker) =>
      tracker.inputs.map((input) => input.key)
    );
    if (new Set(inputs).size !== inputs.length)
      errors.push('Physical input keys must be unique within an inverter.');
  }
  const assigned = new Set<string>();
  const sockets = new Set<string>();
  const incompleteStringIds: string[] = [];
  for (const string of topology.strings) {
    for (const panelId of string.panelIds) {
      if (!panelById.has(panelId))
        errors.push('A string references an unknown panel.');
      if (assigned.has(panelId))
        errors.push('A panel can belong to only one string.');
      assigned.add(panelId);
    }
    if (string.inverterInstanceId == null) {
      if (string.mpptKey != null || string.inputKey != null)
        errors.push(
          'An unconnected string cannot reference an MPPT or physical input.'
        );
    } else {
      const instance = instanceById.get(string.inverterInstanceId);
      const tracker = instance?.trackers.find(
        (item) => item.key === string.mpptKey
      );
      if (
        !instance ||
        !tracker ||
        !tracker.inputs.some((input) => input.key === string.inputKey)
      )
        errors.push(
          'A string references an unknown inverter, MPPT or physical input.'
        );
      const socket = `${string.inverterInstanceId}:${string.inputKey}`;
      if (sockets.has(socket))
        errors.push('A physical DC input can accept only one assigned string.');
      sockets.add(socket);
    }
    if (
      string.incompleteReason ||
      !string.inverterInstanceId ||
      !string.panelIds.length ||
      string.panelIds.some((panelId) => {
        const panel = panelById.get(panelId);
        return !panel?.included || excludedFaceIds.includes(panel.faceId);
      })
    )
      incompleteStringIds.push(string.id);
  }
  const unassignedIncludedPanelCount = panels.filter(
    (panel) =>
      panel.included &&
      !excludedFaceIds.includes(panel.faceId) &&
      !assigned.has(panel.id ?? '')
  ).length;
  return {
    errors: [...new Set(errors)],
    unassignedIncludedPanelCount,
    incompleteStringIds,
    status: 'requires_review' as const,
  };
};

/** One edit atomically moves membership from the previous string. */
export const assignSolarPanelsToString = (
  topology: SolarElectricalTopology,
  stringId: string,
  panelIds: string[]
): SolarElectricalTopology => {
  if (!topology.strings.some((string) => string.id === stringId))
    return topology;
  const moving = new Set(panelIds);
  return {
    ...topology,
    validation: null,
    strings: topology.strings.map((string) => ({
      ...string,
      ...(string.id === stringId ? { incompleteReason: null } : {}),
      panelIds:
        string.id === stringId
          ? [...new Set([...string.panelIds, ...panelIds])]
          : string.panelIds.filter((id) => !moving.has(id)),
    })),
  };
};

export const removeSolarInverterInstance = (
  topology: SolarElectricalTopology,
  id: string
): SolarElectricalTopology => ({
  ...topology,
  validation: null,
  inverterInstances: topology.inverterInstances.filter(
    (instance) => instance.id !== id
  ),
  strings: topology.strings.map((string) =>
    string.inverterInstanceId === id
      ? { ...string, inverterInstanceId: null, mpptKey: null, inputKey: null }
      : string
  ),
});

/** Deletion/repack removes dangling IDs; it never reconnects new panels by position. */
export const reconcileSolarStringPanels = (
  topology: SolarElectricalTopology,
  panels: readonly SolarIdentifiedPanel[],
  repacked = false
): SolarElectricalTopology => {
  const ids = new Set(panels.map((panel) => panel.id));
  const changed = topology.strings.some((string) =>
    string.panelIds.some((id) => !ids.has(id))
  );
  if (!changed) return topology;
  return {
    ...topology,
    validation: null,
    strings: topology.strings.map((string) =>
      string.panelIds.some((id) => !ids.has(id))
        ? {
            ...string,
            panelIds: string.panelIds.filter((id) => ids.has(id)),
            incompleteReason: repacked ? 'repacked' : 'panels_removed',
          }
        : string
    ),
  };
};
